import { useQuery } from "@tanstack/react-query";
import { mcpClient } from "../../lib/mcp-client";

/**
 * Sprint 20 — semantic memory recall. Wraps the `search_memories_semantic` MCP
 * tool: embed a natural-language query, get back the nearest memories ranked by
 * similarity. React Query caches by query + filters. The active session scope is
 * injected automatically by `mcpClient.toolsCall`, so results already honour it.
 */

export interface SemanticMemoryResult {
  id: string;
  name: string;
  memory_type: string | null;
  status: string | null;
  /** Cosine similarity 0–1 for semantic mode; null for the keyword fallback. */
  score: number | null;
  valid_from: string | null;
  valid_until: string | null;
  preview: string;
}

export interface SemanticMemorySearchResponse {
  results: SemanticMemoryResult[];
  total: number;
  mode: "semantic" | "keyword" | "empty";
  filtered_by_scope: number;
}

export interface SemanticMemorySearchParams {
  query: string;
  /** Restrict to memories under one agent (library node id). */
  agent_id?: string;
  memory_types?: string[];
  include_superseded?: boolean;
  limit?: number;
}

const EMPTY: SemanticMemorySearchResponse = { results: [], total: 0, mode: "empty", filtered_by_scope: 0 };

export function useSemanticMemorySearch(params: SemanticMemorySearchParams | null) {
  const query = params?.query.trim() ?? "";
  return useQuery<SemanticMemorySearchResponse>({
    queryKey: ["semantic-memory-search", params?.agent_id ?? null, query, params?.memory_types ?? null, params?.include_superseded ?? false],
    queryFn: async () => {
      const r = await mcpClient.toolsCall<SemanticMemorySearchResponse>("search_memories_semantic", {
        query,
        ...(params?.agent_id ? { agent_id: params.agent_id } : {}),
        ...(params?.memory_types?.length ? { memory_types: params.memory_types } : {}),
        ...(params?.include_superseded ? { include_superseded: true } : {}),
        ...(params?.limit ? { limit: params.limit } : {}),
      });
      if (r.isError) throw new Error(r.content?.[0]?.text ?? "Search failed");
      return r.structuredContent ?? EMPTY;
    },
    enabled: query.length > 0,
    staleTime: 30_000,
  });
}
